import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  Node,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Panel,
  ReactFlowProvider,
} from "reactflow";
import { RouteComponentProps } from "../../../../models/RouteComponent";
import { CardActions, CardOverflow } from "@mui/joy";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { TabPanel, TabView } from "primereact/tabview";
import { ToggleButton } from "primereact/togglebutton";
import useApiService from "../../../../hooks/useApiService";
import { OrganogramRecord, Employee } from "../../../../models/DSF/OrganogramAndAppointments";
import OrganogramNode from "./components/OrganogramNode";
import SearchBox from "./components/SearchBox";
import NodeDetailsModal from "./components/NodeDetailsModal";
import { transformToReactFlowData } from "./utils/organogramUtils";
import { generateOrganogramHTML } from "../../../../service/organogramService";
import { generatePdf } from "../../../../service/pdfGenerator";
import { useResizeObserverErrorHandler } from "../../../../utils/resizeObserverErrorHandler";

// Import ReactFlow CSS
import "reactflow/dist/style.css";
import "./modernOrganogram.scss";

const nodeTypes = {
  organogram: OrganogramNode,
};

function ModernOrganogram(props: RouteComponentProps) {
  // Use the ResizeObserver error handler hook
  useResizeObserverErrorHandler();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredNodes, setFilteredNodes] = useState<Node[]>([]);
  const [isCompact, setIsCompact] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<"TB" | "LR">("TB");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState<any>(null);
  const [expandedStates, setExpandedStates] = useState<Map<string, boolean>>(new Map());

  const toast = useRef<any>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<any>(null);
  const fitViewTimeout = useRef<NodeJS.Timeout | null>(null);
  const { setGlobalLoader, session } = props;

  // Debounced fit view function to prevent ResizeObserver errors
  const debouncedFitView = useCallback((options?: any) => {
    if (fitViewTimeout.current) {
      clearTimeout(fitViewTimeout.current);
    }

    fitViewTimeout.current = setTimeout(() => {
      if (reactFlowInstance.current) {
        try {
          reactFlowInstance.current.fitView({
            padding: 0.05,
            duration: 800,
            minZoom: 0.01,
            maxZoom: 1.2,
            includeHiddenNodes: true,
            ...options,
          });
        } catch (error) {
          // Silently handle any ResizeObserver related errors
          console.warn("FitView operation failed:", error);
        }
      }
    }, 100);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fitViewTimeout.current) {
        clearTimeout(fitViewTimeout.current);
      }
    };
  }, []);

  // Handle toggle expand/collapse for nodes
  const handleToggleExpanded = useCallback((nodeId: string) => {
    setExpandedStates(prevStates => {
      const newStates = new Map(prevStates);
      const currentState = newStates.get(nodeId) ?? true;
      newStates.set(nodeId, !currentState);
      return newStates;
    });
  }, []);

  const { data, loading, error } = useApiService<OrganogramRecord>({
    method: "GET",
    url: "site-organogram",
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
  });

  useEffect(() => {
    setGlobalLoader(loading);
  }, [loading, setGlobalLoader]);

  useEffect(() => {
    if (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load site organogram",
        life: 3000,
      });
    }
  }, [error]);

  // Transform data when received
  useEffect(() => {
    if (data) {
      const { nodes: transformedNodes, edges: transformedEdges } = transformToReactFlowData(
        data,
        isCompact,
        layoutDirection,
        expandedStates,
        handleToggleExpanded
      );

      setNodes(transformedNodes);
      setEdges(transformedEdges);
      setFilteredNodes(transformedNodes);
    }
  }, [data, isCompact, layoutDirection, expandedStates, handleToggleExpanded, setNodes, setEdges]);

  // Handle search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredNodes(nodes);
      return;
    }

    const filtered = nodes.filter(node => {
      const nodeData = node.data;
      const searchLower = searchTerm.toLowerCase();

      return (
        nodeData.label?.toLowerCase().includes(searchLower) ||
        nodeData.title?.toLowerCase().includes(searchLower) ||
        nodeData.legalReference?.toLowerCase().includes(searchLower) ||
        nodeData.employees?.some(
          (emp: Employee) =>
            `${emp.name} ${emp.surname}`.toLowerCase().includes(searchLower) ||
            emp.title?.toLowerCase().includes(searchLower)
        )
      );
    });

    setFilteredNodes(filtered);
  }, [searchTerm, nodes]);

  // Handle node click for highlighting and navigation
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
      setSelectedNodeData(node.data);

      // Center the clicked node using debounced fitView
      debouncedFitView({
        nodes: [node],
        padding: 0.3,
        duration: 800,
      });
    },
    [debouncedFitView]
  );

  // Handle node double-click to show details
  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNodeData(node.data);
    setShowNodeDetails(true);
  }, []);

  // Handle search result selection
  const handleSearchResultClick = useCallback(
    (nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        setSelectedNodeId(nodeId);
        debouncedFitView({
          nodes: [node],
          padding: 0.3,
          duration: 800,
        });
      }
    },
    [nodes, debouncedFitView]
  );

  // Handle search change
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  // Fit view handler - enhanced to show more content
  const handleFitView = useCallback(() => {
    debouncedFitView();
  }, [debouncedFitView]);

  // Re-layout when layout direction changes
  const handleLayoutDirectionChange = useCallback(
    (newDirection: "TB" | "LR") => {
      setLayoutDirection(newDirection);
      // Give React a moment to update the state, then fit view
      setTimeout(() => {
        debouncedFitView();
      }, 100);
    },
    [debouncedFitView]
  );

  // Download functionality
  const handleDownloadPDF = useCallback(async () => {
    if (!data) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "No organogram data available for PDF generation",
        life: 3000,
      });
      return;
    }

    try {
      setGlobalLoader(true);

      // Get company and site information from session
      const companyName = session.client_name || "Company";
      const siteName = session.site_name || "Site";
      const companyLogo = session.client_logo;

      // Generate HTML content for PDF using transformed data
      const { nodes: transformedNodes, edges: transformedEdges } = transformToReactFlowData(
        data,
        isCompact,
        layoutDirection,
        expandedStates
      );

      const htmlContent = await generateOrganogramHTML({
        companyName,
        siteName,
        companyLogo,
        nodes: transformedNodes,
        edges: transformedEdges,
        effectiveDate: new Date(),
      });

      // Generate PDF using the existing service
      await generatePdf(htmlContent, `${siteName}_Organogram.pdf`);

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "PDF generated successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate PDF. Please try again.",
        life: 3000,
      });
    } finally {
      setGlobalLoader(false);
    }
  }, [data, session, setGlobalLoader, isCompact, layoutDirection, expandedStates]);

  return (
    <>
      <TabView>
        <TabPanel header={"Organogram"} key={"organogram"}>
          <CardOverflow style={{ width: "100%" }}>
            <CardActions style={{ marginBottom: 15 }} sx={{ alignSelf: "flex-end", pt: 2 }}>
              <div className="organogram-controls">
                <SearchBox
                  nodes={nodes}
                  onResultSelect={handleSearchResultClick}
                  onSearchChange={handleSearchChange}
                  searchTerm={searchTerm}
                />

                <ToggleButton
                  onLabel="Expanded View"
                  offLabel="Compact View"
                  checked={!isCompact}
                  onChange={e => setIsCompact(!e.value)}
                  className="p-button-sm"
                  style={{ marginRight: 10 }}
                />

                <ToggleButton
                  onLabel="Horizontal Layout"
                  offLabel="Vertical Layout"
                  checked={layoutDirection === "LR"}
                  onChange={e => handleLayoutDirectionChange(e.value ? "LR" : "TB")}
                  className="p-button-sm"
                  style={{ marginRight: 10 }}
                />

                <Button
                  icon="pi pi-expand"
                  label="Fit to Screen"
                  className="p-button-outlined p-button-sm"
                  onClick={handleFitView}
                  style={{ marginRight: 10 }}
                />

                <Button
                  name="button"
                  icon="pi pi-save"
                  label="Download PDF"
                  className="p-button-outlined p-button-sm"
                  style={{ color: "#2dd36f", display: "none" }}
                  onClick={handleDownloadPDF}
                />
              </div>
            </CardActions>
          </CardOverflow>

          <div className="modern-organogram-container" ref={reactFlowWrapper}>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes.map(node => ({
                  ...node,
                  className: selectedNodeId === node.id ? "selected-node" : "",
                }))}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                nodeTypes={nodeTypes}
                connectionMode={ConnectionMode.Loose}
                fitView
                fitViewOptions={{
                  padding: 0.05,
                  includeHiddenNodes: true,
                  minZoom: 0.1,
                  maxZoom: 1.5,
                }}
                minZoom={0.01}
                maxZoom={4}
                attributionPosition="bottom-left"
                onInit={instance => {
                  reactFlowInstance.current = instance;
                }}
              >
                <Background />
                <Controls />
                <MiniMap
                  nodeColor={node => {
                    if (selectedNodeId === node.id) return "#ff6b6b";
                    return node.data?.isEmployee ? "#4ecdc4" : "#45b7d1";
                  }}
                  nodeStrokeWidth={3}
                  zoomable
                  pannable
                />

                {/* Search Results Panel */}
                {searchTerm && (
                  <Panel position="top-right" className="search-results-panel">
                    <div className="search-results">
                      <h4>Search Results ({filteredNodes.length})</h4>
                      <div className="search-results-list">
                        {filteredNodes.slice(0, 10).map(node => (
                          <div
                            key={node.id}
                            className="search-result-item"
                            onClick={() => handleSearchResultClick(node.id)}
                          >
                            <strong>{node.data.label}</strong>
                            {node.data.title && (
                              <div className="result-subtitle">{node.data.title}</div>
                            )}
                            {node.data.legalReference && (
                              <div className="result-legal-reference">
                                {node.data.legalReference}
                              </div>
                            )}
                          </div>
                        ))}
                        {filteredNodes.length > 10 && (
                          <div className="search-results-more">
                            +{filteredNodes.length - 10} more results
                          </div>
                        )}
                      </div>
                    </div>
                  </Panel>
                )}
              </ReactFlow>
            </ReactFlowProvider>
          </div>
        </TabPanel>
      </TabView>

      <NodeDetailsModal
        visible={showNodeDetails}
        onHide={() => setShowNodeDetails(false)}
        nodeData={selectedNodeData}
        routeProps={props}
      />

      <Toast ref={toast} />
    </>
  );
}

export default ModernOrganogram;
